<div class="optimization-diagnostic-layout">
  <p-card>
    <div class="flex align-items-center gap-2 mb-3 flex-wrap">
      <i class="pi pi-exclamation-circle text-xl"></i>
      <h2 class="m-0">Diagnostique — Affaires sans créneau</h2>
      <div class="ml-auto flex gap-2 align-items-center">
        <p-iconfield iconPosition="left">
          <p-inputicon styleClass="pi pi-search" />
          <input
            type="text"
            pInputText
            placeholder="Rechercher une affaire…"
            [value]="searchTerm"
            (input)="onSearchChange($any($event.target).value)"
          />
        </p-iconfield>
        @if (searchTerm) {
          <p-button
            icon="pi pi-times"
            severity="secondary"
            [text]="true"
            [rounded]="true"
            (onClick)="onSearchChange('')"
            pTooltip="Effacer la recherche"
            tooltipPosition="bottom"
          />
        }
        <p-button
          icon="pi pi-refresh"
          label="Actualiser"
          severity="secondary"
          [outlined]="true"
          [loading]="(loading$ | async) ?? false"
          (onClick)="refresh()"
          pTooltip="Recharger le diagnostic"
          tooltipPosition="bottom"
        />
      </div>
    </div>

    <!-- ===== EN-TÊTE DU RUN ===== -->
    @if (data?.run; as run) {
      <div class="flex gap-4 flex-wrap mb-3 text-sm">
        <span
          >Run <span class="font-mono font-semibold">#{{ run.id }}</span></span
        >
        <span class="text-color-secondary">
          Terminé le {{ run.completedAt | date: 'dd/MM/yy HH:mm' }}
        </span>
        @if (coveragePercent(); as pct) {
          <span>
            Couverture :
            <span
              class="font-semibold"
              [class.text-green-500]="pct >= 80"
              [class.text-orange-500]="pct >= 50 && pct < 80"
              [class.text-red-500]="pct < 50"
              >{{ pct }}%</span
            >
            ({{ run.affairsWithSlotsCount }} / {{ run.affairsCount }})
          </span>
        }
        <span class="font-semibold text-red-500">
          {{ run.affairsWithoutSlots }} sans créneau
        </span>
        @if (undiagnosedCount(); as undiag) {
          <span class="text-color-secondary">
            <i class="pi pi-minus-circle mr-1"></i>{{ undiag }} non diagnostiqué·e(s)
          </span>
        }
        @if (!run.diagnosticsFinalizedAt) {
          <span class="text-orange-500">
            <i class="pi pi-spin pi-spinner mr-1"></i>diagnostic en cours…
          </span>
        }
      </div>

      <!-- ===== RÉPARTITION PAR RAISON (filtres) ===== -->
      @if (data?.reasons?.length) {
        <div class="flex gap-2 flex-wrap mb-3">
          @for (reason of data?.reasons; track reason.code) {
            <p-tag
              class="cursor-pointer"
              [class.reason-active]="activeCode === reason.code"
              [class.reason-dimmed]="activeCode && activeCode !== reason.code"
              [value]="reason.label + ' · ' + reason.count"
              [severity]="reasonSeverity(reason.code)"
              (click)="toggleReason(reason)"
              pTooltip="Filtrer sur cette raison"
              tooltipPosition="top"
            ></p-tag>
          }
        </div>
      }
    }

    <!-- ===== TABLE DES AFFAIRES SANS CRÉNEAU ===== -->
    <p-table
      [value]="affairs"
      [loading]="(loading$ | async) ?? false"
      [paginator]="affairs.length > 25"
      [rows]="25"
      [rowsPerPageOptions]="[25, 50, 100]"
      styleClass="p-datatable-sm p-datatable-striped p-datatable-hoverable-rows"
      dataKey="affairId"
    >
      <ng-template pTemplate="header">
        <tr>
          <th>Affaire</th>
          <th>Raison</th>
          <th>CP</th>
          <th>Ville</th>
          <th>Adresse</th>
          <th></th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-affair>
        <tr class="cursor-pointer" (click)="openAffair(affair)">
          <td>
            <div class="font-semibold">{{ affair.name ?? '—' }}</div>
            @if (affair.groupingName) {
              <div class="text-xs text-color-secondary">
                {{ affair.groupingName }}
              </div>
            }
            <div class="text-xs font-mono text-color-secondary">
              #{{ affair.affairId }}
            </div>
          </td>
          <td>
            @if (affair.code) {
              <p-tag
                [value]="affair.label ?? '—'"
                [severity]="reasonSeverity(affair.code)"
              ></p-tag>
              @if (affair.comment) {
                <i
                  class="pi pi-info-circle ml-2 text-color-secondary"
                  [pTooltip]="affair.comment"
                  tooltipPosition="top"
                ></i>
              }
            } @else {
              <span class="text-color-secondary text-sm">
                <i class="pi pi-minus-circle mr-1"></i>Non diagnostiqué
              </span>
            }
          </td>
          <td class="font-mono">{{ affair.postalCode ?? '—' }}</td>
          <td>{{ affair.locality ?? '—' }}</td>
          <td class="text-sm">
            <span class="select-text">{{ affair.mapsQuery ?? '—' }}</span>
          </td>
          <td (click)="$event.stopPropagation()">
            <div class="flex gap-1 justify-content-end align-items-center">
              @if (!affair.code) {
                <p-button
                  icon="pi pi-bolt"
                  label="Diagnostiquer"
                  size="small"
                  severity="warn"
                  [loading]="diagnosingId === affair.affairId"
                  [disabled]="diagnosingId !== null"
                  (onClick)="runDiagnostic(affair, $event)"
                  pTooltip="Analyser pourquoi aucun créneau n'a pu être déterminé lors du dernier run"
                  tooltipPosition="top"
                />
              } @else {
                <p-button
                  icon="pi pi-refresh"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  [loading]="diagnosingId === affair.affairId"
                  [disabled]="diagnosingId !== null"
                  (onClick)="runDiagnostic(affair, $event)"
                  pTooltip="Relancer le diagnostic"
                  tooltipPosition="top"
                />
              }
              <p-button
                [icon]="
                  copiedAffairId === affair.affairId
                    ? 'pi pi-check'
                    : 'pi pi-copy'
                "
                [severity]="
                  copiedAffairId === affair.affairId ? 'success' : 'secondary'
                "
                [text]="true"
                [rounded]="true"
                (onClick)="copyMapsQuery(affair, $event)"
                [pTooltip]="
                  copiedAffairId === affair.affairId
                    ? 'Copié !'
                    : 'Copier adresse (CP + ville)'
                "
                tooltipPosition="top"
              />
              <p-button
                icon="pi pi-map-marker"
                severity="secondary"
                [text]="true"
                [rounded]="true"
                (onClick)="openMaps(affair, $event)"
                pTooltip="Ouvrir dans Google Maps"
                tooltipPosition="top"
              />
              @if (affair.redirectUrl) {
                <p-button
                  icon="pi pi-external-link"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="openRedirect(affair, $event)"
                  pTooltip="Ouvrir l'affaire"
                  tooltipPosition="top"
                />
              }
              @if (affair.code) {
                <p-button
                  icon="pi pi-chevron-right"
                  severity="secondary"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="openAffair(affair)"
                  pTooltip="Détail du diagnostic"
                  tooltipPosition="top"
                />
              }
            </div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center text-color-secondary py-4">
            <i class="pi pi-check-circle mr-2"></i>Aucune affaire sans créneau
          </td>
        </tr>
      </ng-template>
    </p-table>
  </p-card>
</div>
